import { useState } from "react";
import { useLocaleNavigate } from "@/hooks/useLocaleNavigate";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEOHead from "@/components/seo/SEOHead";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { ArrowRight, ArrowLeft } from "lucide-react";
import RegStammdaten from "@/components/registration/RegStammdaten";
import RegKontakte from "@/components/registration/RegKontakte";
import RegService from "@/components/registration/RegService";
import type { RegistrationForm, RegistrationTab } from "@/components/registration/types";
import { INITIAL_REG_FORM, REG_TABS } from "@/components/registration/types";
import { useTranslation } from "react-i18next";

export default function Registrieren() {
  const { t } = useTranslation();
  const navigate = useLocaleNavigate();
  const [activeTab, setActiveTab] = useState<RegistrationTab>("stammdaten");
  const [form, setForm] = useState<RegistrationForm>(INITIAL_REG_FORM);
  const [saving, setSaving] = useState(false);

  const update = (field: string, value: any) =>
    setForm((f) => ({ ...f, [field]: value }));

  const toggleArrayItem = (field: string, item: string) => {
    setForm((f) => {
      const arr = (f as any)[field] as string[];
      return { ...f, [field]: arr.includes(item) ? arr.filter((v) => v !== item) : [...arr, item] };
    });
  };

  const currentIdx = REG_TABS.findIndex((t) => t.id === activeTab);
  const isLast = currentIdx === REG_TABS.length - 1;

  const goNext = () => {
    if (activeTab === "stammdaten") {
      if (!form.company_name || !form.contact_name || !form.contact_email) {
        toast.error(t("registration.fillRequired"));
        return;
      }
    }
    if (!isLast) setActiveTab(REG_TABS[currentIdx + 1].id);
  };

  const goBack = () => {
    if (currentIdx > 0) setActiveTab(REG_TABS[currentIdx - 1].id);
  };

  async function handleSubmit() {
    if (!form.company_name || !form.contact_name || !form.contact_email) {
      toast.error(t("registration.requiredFields"));
      setActiveTab("stammdaten");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.from("provider_registrations").insert([{
        company_name: form.company_name,
        company_type: form.company_type || null,
        legal_name: form.legal_name || null,
        contact_name: form.contact_name,
        contact_role: form.contact_role || null,
        contact_email: form.contact_email,
        phone: form.phone || null,
        website: form.website || null,
        invoice_email: form.invoice_email || null,
        address_line1: form.address_line1 || null,
        postal_code: form.postal_code || null,
        city: form.city || null,
        country: form.country,
        vat_id: form.vat_id || null,
        region: form.region || null,
        service_regions: form.service_regions,
        service_locations: form.service_locations.filter((l) => l.city) as any,
        remote_supported: form.remote_supported,
        categories: form.categories,
        stack_tags: form.selectedStackTags,
        sla_tier: form.sla_tier,
        sla_response_hours: form.sla_response_hours,
        weekly_lead_limit: form.weekly_lead_limit,
        contacts: form.contacts.filter((c) => c.name && c.email) as any,
        provider_type: "craft",
      }]);

      if (error) throw error;

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      fetch(`${supabaseUrl}/functions/v1/send-registration-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${supabaseKey}`,
          apikey: supabaseKey,
        },
        body: JSON.stringify({
          company_name: form.company_name,
          contact_name: form.contact_name,
          contact_email: form.contact_email,
          phone: form.phone || null,
          city: form.city || null,
          region: form.region || null,
          categories: form.categories,
        }),
      }).catch((err) => console.warn("Registration email notification failed:", err));

      navigate("/registrieren/danke");
    } catch (err: any) {
      toast.error(err.message || t("registration.submitError"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEOHead
        title={t("registration.seoTitle")}
        description={t("registration.seoDesc")}
        canonical="/registrieren"
      />
      <Header />

      <main id="main-content" className="flex-1 pt-24 md:pt-28 pb-20 section-padding">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-headline mb-2">{t("registration.title")}</h1>
          <p className="text-caption mb-8">{t("registration.subtitle")}</p>

          <div className="flex border-b border-border mb-8 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-none">
            {REG_TABS.map((tab, idx) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "px-4 md:px-5 py-3 text-sm transition-colors border-b-2 -mb-px whitespace-nowrap min-h-[44px]",
                  activeTab === tab.id
                    ? "border-accent text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                )}
              >
                <span className="text-accent mr-2">{idx + 1}.</span>
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === "stammdaten" && <RegStammdaten form={form} update={update} />}
          {activeTab === "kontakte" && <RegKontakte form={form} update={update} />}
          {activeTab === "service" && <RegService form={form} update={update} toggleArrayItem={toggleArrayItem} />}

          <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
            <Button variant="quiet" onClick={goBack} disabled={currentIdx === 0} className="gap-2">
              <ArrowLeft className="w-4 h-4" /> {t("common.back")}
            </Button>
            {isLast ? (
              <Button variant="commit" onClick={handleSubmit} disabled={saving} className="gap-2">
                {saving ? t("registration.submitting") : t("registration.submitBtn")}
                <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button variant="commit" onClick={goNext} className="gap-2">
                {t("common.next")} <ArrowRight className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
